import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import store from "../store/index";
import useOrder from "../hooks/useOrder";

// ICONS
import previewIcon from "../assets/images/preview.svg";
import clockIcon from "../assets/images/clock.svg";

// COMPONENTS
import RunningKOTStatus from "../components/RunningKOTStatus";
import TabNavigation from "../components/TabNavigation";
import OrderMenu from "./OrderMenu";
import { SUCCESS } from "../assets/constants";
import { useCloud } from "../hooks/useCloud";
import useStore from "../hooks/useStore";
import { usePartnerOrders } from "../hooks/usePartnerOrders";
import toast from "react-hot-toast";
import PartnerKanbanView from "./PartnersOrders";
import NoInternetAlert from "../components/NoInternetAlert";
import { usePrinters } from "../hooks/usePrinters";
import { FormInput } from "../components/FormInput";
import transferIcon from "../assets/images/arrow-left-right.svg";

const TableCard = ({ table, handleTableSelect, room }) => {
  const [tableStatus, setTableStatus] = useState("VACANT");
  const [elapsedTime, setElapsedTime] = useState("");
  const [orderAmount, setOrderAmount] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [isOrderRunning, setIsOrderRunning] = useState(false);
  const [update, setUpdate] = useState(0);

  const fetchTableStatus = async () => {
    const order = store.runningVPA[table.qrpidVPA];
    if (order) {
      const now = new Date();
      const diffMs = now - new Date(order.createdAt);
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMinutes / 60);
      const remainingMinutes = diffMinutes % 60;
      const timeString =
        diffHours > 0
          ? `${diffHours}h ${remainingMinutes}m`
          : `${diffMinutes}m`;

      if (order.customer) {
        setCustomerName(order.customer.name);
      }
      setElapsedTime(timeString);
      setTableStatus(order.status);
      setOrderAmount(order.billTxnLogs?.total || order.amount);
      setIsOrderRunning(true);
    } else {
      setElapsedTime(0);
      setTableStatus("VACANT");
      setOrderAmount(0);
      setCustomerName("");
      setIsOrderRunning(false);
    }
    setUpdate((prev) => prev + 1);
  };
  useEffect(() => {
    fetchTableStatus();
  }, [store.runningVPA]);

  return (
    <div
      onClick={() =>
        handleTableSelect({
          table,
          room,
        })
      }
      key={table.qrpidVPA}
      className={`w-full md:w-[140px] min-h-20 md:min-h-24 cursor-pointer rounded-lg p-1 md:p-2 flex flex-col gap-1 md:gap-2 justify-between relative
      ${
        tableStatus === "VACANT" && "bg-gradient-to-b from-gray-200 to-gray-300"
      }
      ${
        tableStatus === "bill-requested" &&
        "bg-gradient-to-b from-purple-500 to-purple-600 text-white"
      }
      ${
        (tableStatus === "KOTPrint" || tableStatus == "in-process") &&
        "bg-gradient-to-b from-green-500 to-green-600 text-white"
      }
      ${
        tableStatus === "KOTUpdated" &&
        "bg-gradient-to-b from-green-500 to-green-600 text-white"
      }
      ${
        tableStatus === "RequestKOT" &&
        "bg-gradient-to-b from-blue-500 to-blue-600 text-white"
      }`}
    >
      <div className="flex justify-between items-center text-md font-bold">
        {room.roomName != "Parcel" ? (
          <span className="text-sm md:text-lg">T {table.tableNo}</span>
        ) : (
          <span className="text-sm md:text-lg">P {table.tableNo}</span>
        )}
        {tableStatus != "VACANT" && (
          <div className="flex items-center gap-1 text-[10px] md:text-xs">
            <img
              src={clockIcon}
              alt="Clock Icon"
              className="w-1 h-1 md:w-2 md:h-2 filter brightness-0 invert"
            />
            {elapsedTime || "0m"}
          </div>
        )}
      </div>
      {isOrderRunning && (
        <div className="text-xs md:text-sm font-normal">
          <>
            <p className="font-bold">₹ {parseFloat(orderAmount).toFixed(2)} </p>
            <p className="truncate">{customerName}</p>

            {room != "Parcel" && (
              <button className="absolute bottom-0 right-0 pl-1 pt-1 bg-white rounded-tl-lg rounded-none rounded-br-none rounded-bl-none flex items-center justify-center">
                <span className="bg-gray-200 border border-gray-300 p-1 md:p-2 rounded-md">
                  <img
                    src={previewIcon}
                    alt="Preview Icon"
                    className="w-3 h-3 md:w-4 md:h-4"
                  />
                </span>
              </button>
            )}
          </>
        </div>
      )}
    </div>
  );
};

const Switchtable = ({ show, close, updateOrder }) => {
  const { deleteOrder } = useOrder();
  const { rooms, tables, runningVPA } = store;
  const [loading, setLoading] = useState(false);
  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [runningTables, setRunningTables] = useState([]);
  const [emptyTables, setEmptyTables] = useState([]);
  const [tablesMap, setTablesMap] = useState({});

  const getTableLabel = (table) => {
    return table.room === "Parcel" 
      ? `Parcel ${table.tableNo}`
      : `${table.room} - Table ${table.tableNo}`;
  };

  useEffect(() => {
    if (tables.length == 0) return;

    const occupiedTables = Object.keys(runningVPA);
    const runningTables = tables.filter((table) =>
      occupiedTables.includes(table.qrpidVPA)
    );
    const emptyTables = tables.filter(
      (table) => !occupiedTables.includes(table.qrpidVPA)
    );
    setEmptyTables(emptyTables);
    setRunningTables(runningTables);
    setTablesMap(
      tables.reduce((acc, table) => {
        acc[table.qrpidVPA] = table;
        return acc;
      }, {})
    );
  }, [tables, runningVPA]);

  const handleSwitch = async () => {
    try {
      setLoading(true);
      const order = runningVPA[fromValue];
      const typeOfOrder =
        tablesMap[toValue].room === "Parcel" ? "takeaway" : "dining";
      const payload = {
        orderId: order.orderID,
        updates: [
          {
            action: "updateTable",
            newTableVpa: toValue,
          },
          { action: "updateTypeOfOrder", type: typeOfOrder },
        ],
      };

      const { status, data } = await updateOrder(payload);
      if (status === SUCCESS) {
        await deleteOrder(fromValue);
        setFromValue("");
        setToValue("");
        toast.success("Table switched successfully");
        close();
      } else {
        toast.error("Something wemt wrong, please try again");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something wemt wrong, please try again");
    } finally {
      setLoading(false);
    }
  };
  if (!show) {
    return null;
  }
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4 ">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-300">
        {/* Header */}
        <div className="rounded-t-lg overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-5 py-2.5 flex items-center gap-2 border-b border-gray-300 ">
            <div className="bg-white/20 rounded-full p-1">
              <img
                src={transferIcon}
                alt="Transfer"
                className="w-3.5 h-3.5 filter brightness-0 invert"
              />
            </div>
            <h3 className="text-[16px] font-medium text-white tracking-tight">
              Switch Table
            </h3>
            <button
              onClick={close}
              className="ml-auto text-white/80 hover:text-white transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 pt-6 relative">
          <p className="text-[15px] text-gray-700 leading-tight mb-6 text-center">
            Transfer an order from one table to another
          </p>

          <div className="space-y-6">
            {/* From Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-[15px] text-gray-700 font-medium">
                  From Table
                </span>
              </div>
              <FormInput
                type="select"
                label=""
                value={fromValue}
                onChange={setFromValue}
                options={runningTables.map((table) => ({
                  value: table.qrpidVPA,
                  label: getTableLabel(table),
                }))}
                placeholder="Select occupied table"
                className="w-full"
              />
            </div>

            {/* Arrow Icon */}
            <div className="flex justify-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center border border-green-400">
                <img src={transferIcon} alt="Transfer" className="w-5 h-5" />
              </div>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-[15px] text-gray-700 font-medium">
                  To Table
                </span>
              </div>
              <FormInput
                type="select"
                label=""
                value={toValue}
                onChange={setToValue}
                options={emptyTables.map((table) => ({
                  value: table.qrpidVPA,
                  label: getTableLabel(table),
                }))}
                placeholder="Select empty table"
                className="w-full"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={close}
              className="flex-1 px-4 py-2 text-[13px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={loading || !fromValue || !toValue}
              onClick={handleSwitch}
              className="flex-1 px-4 py-2 text-[13px] font-medium bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Switching...</span>
                </div>
              ) : (
                "Switch Table"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TableOverview = () => {
  const { rooms } = store;
  const { getAllOrders } = useOrder();
  const { getCloudSync } = useStore();
  const { printers } = usePrinters();
  const { updateOrder, getMenu, getPrinterConfig } = useCloud();
  const [showRunningKOT, setShowRunningKOT] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [activeTab, setActiveTab] = useState("DineIn");
  const [showTableSwitch, setShowTableSwitch] = useState(false);
  const [producst, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { orders, setOrders } = usePartnerOrders();

  const processMenu = (menu) => {
    let { products, categories } = menu.reduce(
      (acc, category) => {
        if (category.status !== "active") {
          return acc;
        }
        if (category.menuItems.length == 0) {
          return acc;
        }

        acc.products.push(
          ...category.menuItems
            .filter((item) => item.status === "active")
            .map((item) => {
              item.category = category.categoryName;
              item.categoryDetails = category;
              return item;
            })
        );
        if (
          !acc.categories.some(
            (_category) => _category.categoryId == category.categoryId
          )
        )
          acc.categories.push(category);
        return acc;
      },
      {
        products: [],
        categories: [],
      }
    );

    categories = categories.sort((a, b) => a.categoryRank - b.categoryRank);
    products = products.sort((a, b) => a.rank - b.rank);
    return { products, categories };
  };

  const fetchMenu = async () => {
    const menu = await getMenu();
    if (menu && menu.status === SUCCESS) {
      const { data } = menu;

      if (data.length <= 0) {
        return;
      }
      const { products, categories } = processMenu(data);
      const productDeatilsMap = products.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {});

      setProducts(products);
      setCategories(categories);
    }
  };

  useEffect(() => {
    store.setShowRunningKOT = setShowRunningKOT;
  }, []);

  useEffect(() => {
    const fetchAllOrders = async () => {
      const orders = await getAllOrders();
      store.runningVPA = orders.reduce((acc, order) => {
        acc[order.tableVPA] = order;
        return acc;
      }, {});
    };

    setTimeout(() => fetchAllOrders(), 100);
    window.addEventListener("ORDER_DONE", fetchAllOrders);
    window.addEventListener("KOTUpdated", fetchAllOrders);
    window.addEventListener("digitalOrderProcessed", fetchAllOrders);
    window.addEventListener("liveOrder", fetchAllOrders);

    return () => {
      window.removeEventListener("ORDER_DONE", fetchAllOrders);
      window.removeEventListener("KOTUpdated", fetchAllOrders);
      window.removeEventListener("digitalOrderProcessed", fetchAllOrders);
      window.removeEventListener("liveOrder", fetchAllOrders);
    };
  }, []);

  useEffect(() => {
    getCloudSync();
    fetchMenu();
  }, []);

  const hanldeTableSelect = ({ table, room }) => {
    setSelectedTable({
      room: room.roomName,
      table: table.tableNo,
      tableVPA: table.qrpidVPA,
    });
  };

  const renderDineInView = () => {
    return (
      <>
        {selectedTable && (
          <OrderMenu
            data={{
              room: selectedTable.room,
              table: selectedTable.table,
              vpa: selectedTable.tableVPA,
              tableVPA: selectedTable.tableVPA,
            }}
            products={producst}
            categories={categories}
            closeEdit={() => setSelectedTable(null)}
            printers={printers}
          />
        )}

        <div className="p-4 pb-20 md:pb-4 bg-gray-50 overflow-x-hidden relative hide-scrollbar">
          {rooms
            .filter((room) => {
              if (activeTab === "DineIn" && room.roomName != "Parcel") {
                return true;
              } else if (activeTab === "Parcel" && room.roomName == "Parcel") {
                return true;
              } else {
                return false;
              }
            })
            .map((room, roomIndex) => (
              <div
                className="w-full mb-8"
                key={`${room?.roomName}-${roomIndex}`}
              >
                <div className="mb-1">
                  <div
                    className="p-1 border-[0.92px] border-[#16A34A] text-[#16A34A] font-semibold rounded text-center mb-2 inline-block"
                    style={{ minWidth: "150px" }}
                  >
                    {room?.roomName}
                  </div>
                  <div className="flex-1"></div>
                </div>
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-2 md:gap-3"
                  style={{ gap: "20px" }}
                >
                  {room?.tables &&
                    room?.tables.map((table) => (
                      <div
                        key={`${table.qrpidVPA}`}
                        className="w-full md:w-[140px]"
                      >
                        <TableCard
                          key={`${table.qrpidVPA}`}
                          table={table}
                          room={room}
                          handleTableSelect={hanldeTableSelect}
                        />
                      </div>
                    ))}
                </div>
              </div>
            ))}
        </div>

        <Switchtable
          show={showTableSwitch}
          close={() => setShowTableSwitch(false)}
          updateOrder={updateOrder}
        />
      </>
    );
  };

  const renderOtherDeliveriesView = () => {
    return (
      <div className="flex-1 min-h-0">
        <PartnerKanbanView orders={orders} setOrders={setOrders} />
      </div>
    );
  };

  return (
    <div className="bg-gray-50  h-[calc(100vh-50px)] relative">
      {/* INTERNET CONNECTIVITY ALERT */}
      <NoInternetAlert />
      <RunningKOTStatus
        open={showRunningKOT}
        onClose={() => setShowRunningKOT(false)}
      />
      <TabNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setShowRunningKOT={setShowRunningKOT}
        setShowTableSwitch={setShowTableSwitch}
      />

      {["DineIn", "Parcel"].includes(activeTab) && renderDineInView()}
      {["Other"].includes(activeTab) && renderOtherDeliveriesView()}
    </div>
  );
};

export default observer(TableOverview);
